/**
 * Out-of-network insurance, one client at a time.
 *
 *   "It should be like a flow sheet tally at the bottom … it list at the top
 *    the insurance the yearly deductible then net not met and if partially
 *    met it would have the dollar amount. The service a place for billing
 *    code … And then billing info Copay Insurance paid Fees Notes. Is it
 *    possible to set it up as a flow sheet check off or input money and the
 *    actual appointments are put in manually?"
 *
 * Her spreadsheet, kept per patient, is one row per visit: INS, INN, OON,
 * DOS, COPAY, $ REC, Date Of Dep. So this is a sheet and it behaves like one
 * — every cell is typed into directly and saves itself when she leaves it,
 * because a row that needs a dialog opened and closed for each visit is
 * slower than the spreadsheet she is trying to leave behind.
 *
 * INN and OON are her two tick boxes. They are one answer, so ticking either
 * unticks the other and neither can be left in a state the insurer wouldn't
 * recognise.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { Badge, Button, Input, Label, useConfirm, useToast } from "@/components/ui";
import { Panel } from "./MedspaPanels";
import { PanelErrorRow } from "./PanelErrorRow";
import { api, ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

type Network = "inn" | "oon";

interface Policy {
  contact_id: string;
  on_file: boolean;
  carrier: string;
  member_id: string | null;
  group_number: string | null;
  plan_year: number | null;
  deductible_cents: number;
  deductible_prior_cents: number;
  out_of_pocket_max_cents: number | null;
  notes: string | null;
  /** "Patient comes in pays 25 (to us) dollars to verify insurance." */
  verification_cents: number;
  verification_paid_on: string | null;
}

interface Entry {
  id: string;
  contact_id: string;
  appointment_id: string | null;
  service_id: string | null;
  service_name: string;
  date_of_service: string;
  billing_code: string | null;
  network: Network;
  charge_cents: number;
  copay_cents: number;
  insurance_paid_cents: number;
  deposited_on: string | null;
  patient_paid_cents: number;
  deductible_cents: number;
  notes: string | null;
}

interface Totals {
  visits: number;
  charged_cents: number;
  copay_cents: number;
  insurance_paid_cents: number;
  patient_paid_cents: number;
  outstanding_cents: number;
  deductible_cents: number;
  deductible_met_cents: number;
  deductible_remaining_cents: number;
  deductible_state: "none" | "not_met" | "partial" | "met";
}

/** What the BILLER is owed, worked out from her rules. Never stored. */
interface ClaimFee {
  id: string;
  deductible_before_cents: number;
  deductible_after_cents: number;
  meets_deductible: boolean;
  after_deductible: boolean;
  fee_cents: number;
  fee_basis: "flat" | "percent";
  fee_pending: boolean;
}

interface YearTally {
  year: number;
  claims: number;
  claim_fees_cents: number;
  short_year_fee_cents: number;
  short_year_provisional: boolean;
  biller_total_cents: number;
  pending_fees: number;
}

interface Billing {
  fees: Record<string, ClaimFee>;
  years: YearTally[];
  biller_total_cents: number;
  rules: {
    verificationCents: number;
    claimFeeCents: number;
    shortYearFeeCents: number;
    minClaimsPerYear: number;
    afterDeductiblePct: number;
  };
}

interface Sheet { policy: Policy; entries: Entry[]; totals: Totals; billing?: Billing }

/** She types dollars. Everything underneath is cents, like the rest of the software. */
const toCents = (text: string): number => {
  const n = Number(String(text).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
};
const toDollars = (cents: number): string => (cents === 0 ? "" : (cents / 100).toFixed(2));

const todayKey = () => {
  const p = new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: "America/New_York",
  }).formatToParts(new Date());
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}`;
};

/* ─── One cell of the sheet ───────────────────────────────────────────────── */

/**
 * Types like a spreadsheet cell and saves when she leaves it. It does NOT
 * save on every keystroke: a half-typed "12" on the way to "1250" is a real
 * number and would be written down as one.
 */
function Cell({
  value, onCommit, kind = "text", width = "w-28", placeholder, align = "left",
}: {
  value: string;
  onCommit: (next: string) => void;
  kind?: "text" | "money" | "date";
  width?: string;
  placeholder?: string;
  align?: "left" | "right";
}) {
  const [draft, setDraft] = useState(value);
  const dirty = useRef(false);
  useEffect(() => { if (!dirty.current) setDraft(value); }, [value]);

  return (
    <input
      value={draft}
      type={kind === "date" ? "date" : "text"}
      inputMode={kind === "money" ? "decimal" : undefined}
      placeholder={placeholder}
      onChange={(e) => { dirty.current = true; setDraft(e.target.value); }}
      onBlur={() => {
        dirty.current = false;
        if (draft !== value) onCommit(draft);
      }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      className={cn(
        "rounded border border-transparent bg-transparent px-1.5 py-1 text-xs text-zinc-200",
        "hover:border-zinc-700 focus:border-sky-500 focus:bg-zinc-900 focus:outline-none",
        align === "right" && "text-right",
        width,
      )}
    />
  );
}

/** Her INN / OON pair: one answer, drawn as the two boxes she knows. */
function NetworkBoxes({ value, onChange }: { value: Network; onChange: (n: Network) => void }) {
  return (
    <span className="flex items-center gap-2">
      {(["inn", "oon"] as const).map((n) => (
        <label key={n} className="flex cursor-pointer items-center gap-1 text-[11px] text-zinc-400">
          <input
            type="checkbox"
            checked={value === n}
            onChange={() => onChange(n)}
            className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-900 text-sky-500 focus:ring-sky-500"
          />
          {n.toUpperCase()}
        </label>
      ))}
    </span>
  );
}

/* ─── The deductible line ─────────────────────────────────────────────────── */

function DeductibleLine({ totals }: { totals: Totals }) {
  if (totals.deductible_state === "none") {
    return <p className="text-xs text-zinc-500">No yearly deductible recorded for this policy.</p>;
  }
  const pct = totals.deductible_cents === 0
    ? 0
    : Math.min(100, Math.round((totals.deductible_met_cents / totals.deductible_cents) * 100));
  const label = totals.deductible_state === "met" ? "Met"
    : totals.deductible_state === "not_met" ? "Not met"
    : "Partly met";
  const colour = totals.deductible_state === "met" ? "emerald"
    : totals.deductible_state === "not_met" ? "rose"
    : "amber";
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-semibold text-zinc-100">
          {formatMoney(totals.deductible_cents)} deductible
        </span>
        <Badge color={colour} size="xs">{label}</Badge>
        {/* "if partially met it would have the dollar amount" */}
        <span className="text-xs text-zinc-400">
          {formatMoney(totals.deductible_met_cents)} met
          {totals.deductible_remaining_cents > 0 && (
            <> · {formatMoney(totals.deductible_remaining_cents)} to go</>
          )}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
        <div
          className={cn("h-full rounded-full",
            colour === "emerald" ? "bg-emerald-500" : colour === "amber" ? "bg-amber-500" : "bg-rose-500")}
          style={{ width: `${pct}%` }}
        />
      </div>
      {totals.deductible_state !== "met" && (
        <p className="mt-1.5 text-[11px] text-zinc-500">
          Until it is met, the client pays. After that the insurer starts paying.
        </p>
      )}
    </div>
  );
}

/**
 * What the biller earns on one visit, and why.
 *
 * Two answers only, and which one it is turns entirely on where the
 * deductible stood BEFORE this visit: a flat fee while it is still being met
 * (including the visit that finishes it off), a share of the payout after
 * that. Saying which is as important as saying how much, because she is the
 * one who has to explain it to him.
 */
function BillerFee({ fee, rules }: { fee?: ClaimFee; rules?: Billing["rules"] }) {
  if (!fee) return <span className="text-zinc-600">—</span>;
  if (fee.fee_pending) {
    return (
      <span className="whitespace-nowrap text-[11px] text-zinc-500" title="A share of a payout that has not landed yet">
        waiting on the payout
      </span>
    );
  }
  const why = fee.fee_basis === "percent"
    ? `${rules?.afterDeductiblePct ?? 10}% of what the insurer paid, because the deductible was already met`
    : fee.meets_deductible
      ? "Flat, and this is the claim that met the deductible"
      : "Flat, while the deductible is still being met";
  return (
    <span className="whitespace-nowrap tabular-nums text-amber-300" title={why}>
      {formatMoney(fee.fee_cents)}
      <span className="ml-1 text-[10px] text-zinc-500">{fee.fee_basis === "percent" ? "%" : "flat"}</span>
    </span>
  );
}

/**
 * The year's bill from the biller, underneath the sheet.
 *
 *   "we only billed 2 sessions. My fee is 10 dollars plus 5 for each bill
 *    total 20 dollars."
 *
 * The shortfall is kept on its own line rather than spread over the claims,
 * because a third claim takes it away again and a number that can still move
 * has to say so.
 */
function BillerTally({ billing, year }: { billing?: Billing; year?: number }) {
  const y = billing?.years.find((x) => x.year === year) ?? billing?.years[0];
  if (!billing || !y) return null;
  return (
    <div className="rounded-md border border-amber-500/25 bg-amber-500/5 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-300">
          Owed to the biller · {y.year}
        </span>
        <span className="text-sm font-semibold tabular-nums text-amber-200">
          {formatMoney(y.biller_total_cents)}
        </span>
      </div>
      <dl className="mt-1.5 space-y-0.5 text-[11px] text-zinc-400">
        <div className="flex justify-between gap-3">
          <dt>{y.claims} {y.claims === 1 ? "claim" : "claims"}</dt>
          <dd className="tabular-nums">{formatMoney(y.claim_fees_cents)}</dd>
        </div>
        {y.short_year_fee_cents > 0 && (
          <div className="flex justify-between gap-3 text-amber-300/90">
            <dt>
              Under {billing.rules.minClaimsPerYear} claims this year
              {y.short_year_provisional && (
                <span className="text-zinc-500"> · one more takes this off</span>
              )}
            </dt>
            <dd className="tabular-nums">{formatMoney(y.short_year_fee_cents)}</dd>
          </div>
        )}
        {y.pending_fees > 0 && (
          <div className="text-zinc-500">
            {y.pending_fees} {y.pending_fees === 1 ? "claim is" : "claims are"} waiting on a payout before
            the {billing.rules.afterDeductiblePct}% can be worked out.
          </div>
        )}
      </dl>
    </div>
  );
}

/* ─── The panel ───────────────────────────────────────────────────────────── */

export function InsurancePanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSheet(await api.get<Sheet>(`/insurance/${contactId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load the insurance sheet");
    }
  }, [contactId]);
  useEffect(() => { void load(); }, [load]);

  /* The tally is the server's, so the row totals and the sheet can never
     drift apart; every save hands a fresh one back. */
  const refresh = useCallback(async () => {
    try { setSheet(await api.get<Sheet>(`/insurance/${contactId}`)); } catch { /* keep what is on screen */ }
  }, [contactId]);

  const patchEntry = async (id: string, patch: Partial<Record<keyof Entry, unknown>>) => {
    // Show it immediately, then let the server's totals land.
    setSheet((s) => s && ({
      ...s,
      entries: s.entries.map((e) => (e.id === id ? { ...e, ...patch } as Entry : e)),
    }));
    try {
      await api.patch(`/insurance/entries/${id}`, patch);
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
      await refresh();
    }
  };

  const addRow = async () => {
    setAdding(true);
    try {
      await api.post("/insurance/entries", {
        contact_id: contactId,
        date_of_service: todayKey(),
        network: "oon",
      });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add a visit");
    } finally {
      setAdding(false);
    }
  };

  const removeRow = async (e: Entry) => {
    const ok = await confirm({
      title: "Remove this visit?",
      description: <>The {e.date_of_service} visit comes off this sheet. It does not touch the appointment or the money already taken.</>,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/insurance/entries/${e.id}`);
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove that visit");
    }
  };

  const totals = sheet?.totals;
  const entries = useMemo(() => sheet?.entries ?? [], [sheet]);

  return (
    <div className="space-y-4">
      <Panel
        title="Insurance"
        action={
          <button
            type="button"
            onClick={() => setEditingPolicy((v) => !v)}
            className="text-[11px] font-medium text-sky-300 hover:text-sky-200"
          >
            {editingPolicy ? "Done" : "Edit policy"}
          </button>
        }
      >
        {error ? (
          <PanelErrorRow message={error} onRetry={load} />
        ) : !sheet ? (
          <p className="px-4 py-3 text-xs text-zinc-500">Loading…</p>
        ) : editingPolicy ? (
          <PolicyForm
            contactId={contactId}
            policy={sheet.policy}
            onSaved={(next) => { setSheet(next); setEditingPolicy(false); }}
          />
        ) : (
          <div className="space-y-3 px-4 py-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-sm font-semibold text-zinc-100">
                {sheet.policy.carrier || "No carrier recorded"}
              </span>
              {sheet.policy.member_id && (
                <span className="text-xs text-zinc-400">Member {sheet.policy.member_id}</span>
              )}
              {sheet.policy.group_number && (
                <span className="text-xs text-zinc-500">Group {sheet.policy.group_number}</span>
              )}
              {sheet.policy.plan_year && (
                <span className="text-xs text-zinc-500">{sheet.policy.plan_year}</span>
              )}
            </div>
            {totals && <DeductibleLine totals={totals} />}
            {/* "To log name and payments received the 25 dollars" */}
            {sheet.policy.verification_cents > 0 && (
              <p className="text-xs text-emerald-300">
                {formatMoney(sheet.policy.verification_cents)} taken to verify the policy
                {sheet.policy.verification_paid_on && (
                  <span className="text-zinc-500"> · {sheet.policy.verification_paid_on}</span>
                )}
              </p>
            )}
            <BillerTally billing={sheet.billing} year={sheet.billing?.years[0]?.year} />
            {sheet.policy.notes && (
              <p className="whitespace-pre-wrap rounded-md bg-zinc-900/60 px-2.5 py-2 text-xs text-zinc-300">
                {sheet.policy.notes}
              </p>
            )}
          </div>
        )}
      </Panel>

      <Panel
        title="Visits billed"
        action={
          <Button size="sm" variant="ghost" onClick={() => void addRow()} disabled={adding}>
            <Plus className="h-3.5 w-3.5" /> Add a visit
          </Button>
        }
      >
        {!sheet ? (
          <p className="px-4 py-3 text-xs text-zinc-500">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-zinc-500">
            Nothing billed yet. Add a visit and type straight into the row.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] border-collapse text-xs">
              <thead>
                <tr className="border-b border-zinc-800/70 text-[11px] uppercase tracking-wide text-zinc-500">
                  <th className="px-2 py-2 text-left font-medium">DOS</th>
                  <th className="px-2 py-2 text-left font-medium">Service</th>
                  <th className="px-2 py-2 text-left font-medium">Code</th>
                  <th className="px-2 py-2 text-left font-medium">Network</th>
                  <th className="px-2 py-2 text-right font-medium">Billed</th>
                  <th className="px-2 py-2 text-right font-medium">Copay</th>
                  <th className="px-2 py-2 text-right font-medium">Insurance paid</th>
                  <th className="px-2 py-2 text-left font-medium">Deposited</th>
                  <th className="px-2 py-2 text-right font-medium">Client paid</th>
                  <th className="px-2 py-2 text-right font-medium">To deductible</th>
                  {/* "fees (not my fees) the billers and tally's" */}
                  <th className="px-2 py-2 text-right font-medium">Biller fee</th>
                  <th className="px-2 py-2 text-left font-medium">Notes</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-800/40 hover:bg-zinc-900/30">
                    <td className="px-1 py-0.5">
                      <Cell kind="date" width="w-32" value={e.date_of_service}
                        onCommit={(v) => void patchEntry(e.id, { date_of_service: v })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell width="w-44" placeholder="Treatment" value={e.service_name}
                        onCommit={(v) => void patchEntry(e.id, { service_name: v })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell width="w-20" placeholder="CPT" value={e.billing_code ?? ""}
                        onCommit={(v) => void patchEntry(e.id, { billing_code: v || null })} />
                    </td>
                    <td className="px-2 py-0.5">
                      <NetworkBoxes value={e.network}
                        onChange={(n) => void patchEntry(e.id, { network: n })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell kind="money" align="right" width="w-20" placeholder="0.00"
                        value={toDollars(e.charge_cents)}
                        onCommit={(v) => void patchEntry(e.id, { charge_cents: toCents(v) })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell kind="money" align="right" width="w-20" placeholder="0.00"
                        value={toDollars(e.copay_cents)}
                        onCommit={(v) => void patchEntry(e.id, { copay_cents: toCents(v) })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell kind="money" align="right" width="w-24" placeholder="0.00"
                        value={toDollars(e.insurance_paid_cents)}
                        onCommit={(v) => void patchEntry(e.id, { insurance_paid_cents: toCents(v) })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell kind="date" width="w-32" value={e.deposited_on ?? ""}
                        onCommit={(v) => void patchEntry(e.id, { deposited_on: v || null })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell kind="money" align="right" width="w-20" placeholder="0.00"
                        value={toDollars(e.patient_paid_cents)}
                        onCommit={(v) => void patchEntry(e.id, { patient_paid_cents: toCents(v) })} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell kind="money" align="right" width="w-24" placeholder="0.00"
                        value={toDollars(e.deductible_cents)}
                        onCommit={(v) => void patchEntry(e.id, { deductible_cents: toCents(v) })} />
                    </td>
                    <td className="px-2 py-0.5 text-right">
                      <BillerFee fee={sheet.billing?.fees[e.id]} rules={sheet.billing?.rules} />
                    </td>
                    <td className="px-1 py-0.5">
                      <Cell width="w-48" placeholder="…" value={e.notes ?? ""}
                        onCommit={(v) => void patchEntry(e.id, { notes: v || null })} />
                    </td>
                    <td className="px-2 py-0.5 text-right">
                      <button
                        type="button"
                        onClick={() => void removeRow(e)}
                        aria-label={`Remove the ${e.date_of_service} visit`}
                        className="rounded p-1 text-zinc-600 hover:bg-zinc-800 hover:text-rose-300"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* "a flow sheet tally at the bottom" */}
              {totals && (
                <tfoot>
                  <tr className="border-t-2 border-zinc-700 bg-zinc-900/50 font-semibold text-zinc-100">
                    <td className="px-2 py-2" colSpan={4}>
                      {totals.visits} {totals.visits === 1 ? "visit" : "visits"}
                    </td>
                    <td className="px-2 py-2 text-right">{formatMoney(totals.charged_cents)}</td>
                    <td className="px-2 py-2 text-right">{formatMoney(totals.copay_cents)}</td>
                    <td className="px-2 py-2 text-right">{formatMoney(totals.insurance_paid_cents)}</td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right">{formatMoney(totals.patient_paid_cents)}</td>
                    <td className="px-2 py-2 text-right">{formatMoney(totals.deductible_met_cents)}</td>
                    <td className="px-2 py-2 text-right text-amber-300">
                      {formatMoney(sheet?.billing?.biller_total_cents ?? 0)}
                    </td>
                    <td className="px-2 py-2 text-[11px] font-normal text-zinc-400" colSpan={2}>
                      {totals.outstanding_cents > 0
                        ? `${formatMoney(totals.outstanding_cents)} still out`
                        : "Nothing outstanding"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ─── The policy at the top ───────────────────────────────────────────────── */

function PolicyForm({
  contactId, policy, onSaved,
}: { contactId: string; policy: Policy; onSaved: (sheet: Sheet) => void }) {
  const toast = useToast();
  const [carrier, setCarrier] = useState(policy.carrier);
  const [memberId, setMemberId] = useState(policy.member_id ?? "");
  const [group, setGroup] = useState(policy.group_number ?? "");
  const [year, setYear] = useState(policy.plan_year ? String(policy.plan_year) : "");
  const [deductible, setDeductible] = useState(toDollars(policy.deductible_cents));
  const [prior, setPrior] = useState(toDollars(policy.deductible_prior_cents));
  const [notes, setNotes] = useState(policy.notes ?? "");
  /* "Patient comes in pays 25 (to us) dollars to verify insurance" — money she
     takes, logged against the policy it paid for. */
  const [verified, setVerified] = useState(toDollars(policy.verification_cents));
  const [verifiedOn, setVerifiedOn] = useState(policy.verification_paid_on ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const next = await api.put<Sheet>(`/insurance/${contactId}`, {
        carrier: carrier.trim(),
        member_id: memberId.trim() || null,
        group_number: group.trim() || null,
        plan_year: year.trim() ? Number(year.trim()) : null,
        deductible_cents: toCents(deductible),
        deductible_prior_cents: toCents(prior),
        notes: notes.trim() || null,
        verification_cents: toCents(verified),
        verification_paid_on: verifiedOn || null,
      });
      toast.success("Policy saved");
      onSaved(next);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 px-4 py-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Insurance</Label>
          <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="NYSHIP" />
        </div>
        <div>
          <Label>Member ID</Label>
          <Input value={memberId} onChange={(e) => setMemberId(e.target.value)} />
        </div>
        <div>
          <Label>Group</Label>
          <Input value={group} onChange={(e) => setGroup(e.target.value)} />
        </div>
        <div>
          <Label>Plan year</Label>
          <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" inputMode="numeric" />
        </div>
        <div>
          <Label>Yearly deductible</Label>
          <Input value={deductible} onChange={(e) => setDeductible(e.target.value)}
            placeholder="2000.00" inputMode="decimal" />
        </div>
        <div>
          <Label>Already met before us</Label>
          <Input value={prior} onChange={(e) => setPrior(e.target.value)}
            placeholder="0.00" inputMode="decimal" />
          {/* A client who comes across in August has met most of their year
              somewhere else, and a sheet that starts from zero would have
              her chasing money the insurer is already paying. */}
          <p className="mt-1 text-[11px] text-zinc-500">
            What they had already put against this year's deductible elsewhere.
          </p>
        </div>
        <div>
          <Label>Paid to verify the policy</Label>
          <Input value={verified} onChange={(e) => setVerified(e.target.value)}
            placeholder="25.00" inputMode="decimal" />
          <p className="mt-1 text-[11px] text-zinc-500">
            What this client paid you up front. It is yours, not the biller's,
            so it never counts toward a fee.
          </p>
        </div>
        <div>
          <Label>Paid on</Label>
          <Input type="date" min="1900-01-01" max="2200-12-31"
            value={verifiedOn} onChange={(e) => setVerifiedOn(e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering about this policy" />
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save policy"}
        </Button>
      </div>
    </div>
  );
}
